import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Button, Stack, Typography } from '@mui/material';
import type { ExerciseSessionControls } from 'Phaser/Scenes/OSMDExerciseScene';
import ExercisePlayShell from './ExercisePlayShell';
import { resolvePlaySession } from './resolvePlaySession';
import { SCALES_EXERCISE_SLUG } from './exerciseReadModel';
import { useExerciseContent } from './useExerciseContent';
import { writtenCellName } from 'Seeds/ExerciseContent/writtenCellName';

/**
 * The real, user-facing play screen for the Exercises section (MUS-481). Reached
 * by tapping a level's tier on the overview (`/exercises`), NOT by a debug URL. It
 * resolves the tapped level/tier/key, against the exercise the server holds (read by
 * slug), into a sitting and mounts the shared play shell (scene + host MIDI bridge +
 * return-nav) with real Start / Stop / Next / End controls. Until the read answers it
 * renders nothing; a read that fails, or a tap that does not resolve, goes back to the
 * overview.
 *
 * Real multi-take loop: Start opens the first take. A take ends when the music ends:
 * once the last note's release window closes, the transport and metronome stop and
 * the played notes are graded (the scene renders the gradient) - there is no Submit.
 * Stop leaves a take early; a stopped take is never graded and never counts. Next
 * advances to the next take, and repeating takes is what makes the cumulative-clear
 * and mastery-streak bars reachable - so the earned point writes and the star lights
 * on the overview. End finishes the sitting, and the return-nav sends the user back
 * to `/exercises`.
 *
 * Deliberately minimal chrome: no write-state / frontier debug readouts and no
 * per-take progression HUD (the take counter / "2 clean, 1 to go" presentation is
 * MUS-482). This is the thin real spine that closes the loop.
 *
 * Next is enabled only once the take has ended - the machine admits NEXT from its
 * passed / failed / no-input / stopped states (never mid-take), so the control
 * tracks that window rather than sitting always-live like the dev launcher's debug
 * button. The scene reports every take end (graded, no input, or stopped) through
 * the shell's `onTakeEnded`, which covers all of them uniformly and never strands
 * the user.
 *
 * Registered flag-gated on USE_EXERCISES in App.tsx (never in production, where the
 * flag is force-off); the shell is dark behind the same flag as defense in depth.
 */

/** Where the take loop is, as far as the controls are concerned. */
type TakePhase =
  | 'idle' // before the first Start - only Start is live
  | 'inTake' // a take is open (playing / awaiting input) - Stop is live
  | 'canAdvance' // the take ended (machine in passed/failed/noInput/stopped) - Next is live
  | 'done'; // the sitting was ended - the return-nav takes over

const ExercisePlay: React.FC = () => {
  const params = useParams<{ levelNumber?: string; tierNumber?: string; tonic?: string }>();
  const levelNumber = Number(params.levelNumber);
  const tierParsed = Number(params.tierNumber);
  const tierNumber = Number.isFinite(tierParsed) ? tierParsed : undefined;
  const tonic = params.tonic;
  const { status, content } = useExerciseContent(SCALES_EXERCISE_SLUG);

  const session = React.useMemo(
    () =>
      content && Number.isFinite(levelNumber)
        ? resolvePlaySession(content, levelNumber, tierNumber, tonic)
        : null,
    [content, levelNumber, tierNumber, tonic],
  );

  const [phase, setPhase] = React.useState<TakePhase>('idle');
  // A take that ended after the sitting was ended does not reopen Next.
  const onTakeEnded = React.useCallback(
    () => setPhase((current) => (current === 'inTake' ? 'canAdvance' : current)),
    [],
  );

  // The exercise is still being read: nothing to mount yet.
  if (!content && status === 'loading') return null;
  // Unknown level/tier (a hand-typed or stale URL), or the read failed: fall back to
  // the overview rather than mounting a scene with no content.
  if (!session) return <Navigate to="/exercises" replace />;

  return (
    <ExercisePlayShell
      session={session}
      onTakeEnded={onTakeEnded}
      containerProps={{ 'data-exercise-play': true }}
    >
      {(controls: ExerciseSessionControls | null) => {
        const ready = !!controls;
        return (
          <>
            {/* The cell as it is written: the Bb Superlocrian cell reads "A# Superlocrian". */}
            <Typography
              component="h1"
              data-exercise-play-title
              sx={{
                position: 'fixed',
                top: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 20,
                fontSize: 18,
                color: '#e8e8e8',
              }}
            >
              {writtenCellName(session.cell)}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                position: 'fixed',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 20,
              }}
            >
              <Button
                variant="contained"
                disabled={!ready || phase !== 'idle'}
                onClick={() => {
                  controls?.start();
                  setPhase('inTake');
                }}
              >
                Start
              </Button>
              <Button
                variant="contained"
                disabled={!ready || phase !== 'inTake'}
                onClick={() => controls?.stopTake()}
              >
                Stop
              </Button>
              <Button
                variant="contained"
                disabled={!ready || phase !== 'canAdvance'}
                onClick={() => {
                  controls?.next();
                  setPhase('inTake');
                }}
              >
                Next
              </Button>
              <Button
                variant="contained"
                disabled={!ready || phase === 'idle' || phase === 'done'}
                onClick={() => {
                  controls?.end();
                  setPhase('done');
                }}
              >
                End
              </Button>
            </Stack>
          </>
        );
      }}
    </ExercisePlayShell>
  );
};

export default ExercisePlay;
